import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack, MdLaunch } from 'react-icons/md';
import moment from 'moment';
import axios from 'config/axios';
import ButtonLink from 'components/ButtonLink';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';

import './index.css';

function AssignmentReport() {
    const [assignmentData, setAssignmentData] = useState(null);

    const { assignmentId } = useParams();
    const { organization } = useAppSelector(state => state.organization);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    useEffect(() => {
        axios.get(`questions/assignment/report/${assignmentId}/`)
            .then(res => {
                // console.log(res.data);
                setAssignmentData(res.data);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    if (!assignmentData) return <></>;

    return (
        <div className='layout'>
        <div className='assignmentReport'>
            <Helmet>
                <title>
                    Assignment Report - {organization.name}
                </title>
            </Helmet>
            <div className='assignmentReport__bar'>
                <button
                    className='assignmentReport__back'
                    onClick={() => navigate('/assignments')}
                >
                    <MdOutlineArrowBack className='assignmentReport__backIcon' />
                    Back to My Assignments
                </button>
                {/* <h2 className='digitalReport__studentName'>
                    {testData.studentName}
                </h2> */}
            </div>
            <div className='assignmentReport__headerGroup'>
                <div className='assignmentReport__headerSection'>
                    {assignmentData.logo && 
                        <img
                            className='assignmentReport__headerLogo'
                            alt='logo'
                            src={assignmentData.logo}
                        />
                    }
                </div>
                <div className='assignmentReport__headerSection'>
                    <div className='assignmentReport__headerInfo'>
                        <p>
                            Name: {assignmentData.studentName}
                        </p>
                        <p>
                            Assignment name: {assignmentData.name}
                        </p>
                        <p>
                            Completed on: {moment(assignmentData.completedDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='assignmentReport__headerGroup'>
                <h1 className='assignmentReport__headerTitle'>
                    Assignment Result Overview
                </h1>
                <div className='assignmentReport__buttonGroup'>
                    {assignmentData.reviewLink &&
                        <ButtonLink
                            className='assignmentReport__button'
                            secondary
                            href={assignmentData.reviewLink}
                            newTab
                        >
                            VIDEO REVIEW &nbsp; <MdLaunch />
                        </ButtonLink>
                    }
                    <ButtonLink className='assignmentReport__button--blue' to={`/assignments/review/${assignmentId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                </div>
            </div>
            <div className='assignmentReport__questions'>
                <div className='assignmentReport__questionsOverview'>
                    <div className='assignmentReport__questionsSummary'>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.filter(answer => answer.isCorrect).length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='assignmentReport__questionsSummaryItem'>
                            <p className='assignmentReport__questionSummaryNumber'>
                                {assignmentData.answers.filter(answer => !answer.isCorrect).length}
                            </p>
                            <p className='assignmentReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    <table className='assignmentReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') && <th>Correct Answer</th>}
                                <th>Your Answer</th>
                                <th>Category</th>
                                <th>Time Spent</th>
                            </tr>
                            {assignmentData.answers.map((answer, a) => (
                                <tr key={assignmentData.name + answer.questionNumber}>
                                    <td>{a + 1}</td>
                                    {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                        <td>{answer.correctChoice !== null
                                            ?   MULTIPLE_CHOICES[answer.correctChoice]
                                            :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                        }</td>
                                    }
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='assignmentReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    <td>
                                        <p className='assignmentReport__timeSpentGood'>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className='assignmentReport__questionsPrint'>
                <div className='assignmentReport__questionsOverview'>
                    <p className='assignmentReport__moduleName'>
                        {assignmentData.name}
                    </p>
                    <table className='assignmentReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') && <th>Correct Answer</th>}
                                <th>Your Answer</th>
                                <th>Category</th>
                                <th>Time Spent</th>
                            </tr>
                            {assignmentData.answers.map((answer, a) => (
                                <tr key={assignmentData.name + answer.questionNumber}>
                                    <td>{a + 1}</td>
                                    {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                        <td>{answer.correctChoice !== null
                                            ?   MULTIPLE_CHOICES[answer.correctChoice]
                                            :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                        }</td>
                                    }
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='assignmentReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='assignmentReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>{answer.category}</td>
                                    <td>
                                        <p className='assignmentReport__timeSpentGood'>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </div>
    );
}

export default AssignmentReport;